import { Link } from 'react-router-dom';
import type { ApiReconstructedDocumentListItem } from '@avisoma/shared';
import { formatDateOnly } from '../../lib/dateFormat';
import { summariseExtraction } from '../../components/reconstructedDocument/summariseExtraction';
import { InfinityScroll } from '../../components/InfinityScroll';
import SortableTable, { type SortableTableColumn } from '../../components/SortableTable';
import { ClassificationCell } from '../../components/tableCells';

const columns: SortableTableColumn<ApiReconstructedDocumentListItem>[] = [
  {
    label: 'Classification',
    accessor: 'classification',
    render: (item) => (
      <ClassificationCell
        classification={item.classification}
        certainty={item.classificationCertainty}
        overrideCategory={item.userSuggestedCategory}
      />
    ),
  },
  {
    label: 'Suggested Filename',
    accessor: 'suggestedFilename',
    render: (item) => {
      const truncatedId = `${item.id.slice(0, 8)}…${item.id.slice(-4)}`;
      const hintParts: string[] = [];
      if (item.sourceHints.esn) hintParts.push(`ESN ${item.sourceHints.esn}`);
      if (item.sourceHints.tsn) hintParts.push(`TSN ${item.sourceHints.tsn}`);
      if (item.sourceHints.csn) hintParts.push(`CSN ${item.sourceHints.csn}`);
      const hintLine = hintParts.join(' · ');
      return (
        <div className="min-w-0">
          <Link
            to={`/reconstructed-documents/${item.id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-blue-600 dark:text-blue-400 truncate block"
            title={item.suggestedFilename}
          >
            {item.suggestedFilename}
          </Link>
          <div className="text-xs font-mono text-gray-500 dark:text-zinc-500">{truncatedId}</div>
          {hintLine && (
            <div className="text-xs text-gray-500 dark:text-zinc-400 mt-1">{hintLine}</div>
          )}
        </div>
      );
    },
  },
  {
    label: 'Pages',
    accessor: 'pageCount',
    className: 'text-right',
    render: (item) => <span className="block text-right">{item.pageCount}</span>,
  },
  {
    label: 'Sources',
    accessor: 'sourceDocumentCount',
    className: 'text-right',
    render: (item) => <span className="block text-right">{item.sourceDocumentCount}</span>,
  },
  {
    label: 'Extraction',
    accessor: 'consolidatedExtraction',
    render: (item) => summariseExtraction(item.consolidatedExtraction),
  },
  {
    label: 'Created',
    accessor: 'createdAt',
    render: (item) => (
      <span className="text-xs text-gray-500 dark:text-zinc-400">
        {formatDateOnly(item.createdAt)}
      </span>
    ),
  },
];

export function ReconstructedDocumentsTableSection({
  items,
  loading,
  hasMore,
  onLoadMore,
}: {
  items: ApiReconstructedDocumentListItem[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}) {
  return (
    <div className="bg-white dark:bg-zinc-800 dark:border dark:border-zinc-700 shadow sm:rounded-lg overflow-hidden">
      {items.length === 0 ? (
        <div className="px-4 py-10 text-sm text-center text-gray-500 dark:text-zinc-400">
          {loading ? 'Loading…' : 'No reconstructed documents match these filters.'}
        </div>
      ) : (
        <>
          <SortableTable
            storageId="reconstructed-documents"
            columns={columns}
            data={items}
            rowKey={(item) => item.id}
            rowHref={(item) => `/reconstructed-documents/${item.id}`}
            limitHeight={false}
          />

          <InfinityScroll
            enabled={hasMore && !loading}
            onLoadMore={onLoadMore}
            rootMargin="400px"
          />
        </>
      )}
    </div>
  );
}
